import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GanttChart, Plus, Edit2, Trash2, Loader2, RefreshCw, Sprout } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = [
  { v: 'planned', l: 'Planned', cls: 'bg-slate-700/40 border-slate-700 text-slate-300' },
  { v: 'in_progress', l: 'In progress', cls: 'bg-sky-500/15 border-sky-500/40 text-sky-300' },
  { v: 'completed', l: 'Completed', cls: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
  { v: 'overdue', l: 'Overdue', cls: 'bg-rose-500/15 border-rose-500/40 text-rose-300' },
];

const blank = {
  element: '1 - Safety Policy & Objectives',
  sub_element: '',
  description: '',
  owner: '',
  start_date: '',
  target_date: '',
  completion_date: '',
  status: 'planned',
  progress_pct: 0,
};

export default function SMSImplementationPlanPage() {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], summary: { total: 0, completed: 0, in_progress: 0, overdue: 0, avg_progress_pct: 0 } });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const token = localStorage.getItem('token');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/gaca-p1/sms-plan`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await r.json());
    } catch { toast.error('Failed to load plan'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const seedBaseline = async () => {
    try {
      const res = await fetch(`${API}/api/gaca-p1/sms-plan/seed`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const j = await res.json();
      toast.success(j.message || `Seeded ${j.created} items`);
      fetchAll();
    } catch { toast.error('Seed failed'); }
  };

  const submit = async () => {
    if (!form.sub_element.trim() || !form.description.trim()) { toast.error('Sub-element and description are required'); return; }
    const payload = { ...form, progress_pct: Number(form.progress_pct) || 0 };
    try {
      const url = editing ? `${API}/api/gaca-p1/sms-plan/${editing}` : `${API}/api/gaca-p1/sms-plan`;
      const method = editing ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success(editing ? 'Item updated' : 'Item added');
      setOpen(false); setEditing(null); setForm(blank);
      fetchAll();
    } catch { toast.error('Save failed'); }
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      element: it.element || '1 - Safety Policy & Objectives',
      sub_element: it.sub_element || '',
      description: it.description || '',
      owner: it.owner || '',
      start_date: it.start_date || '',
      target_date: it.target_date || '',
      completion_date: it.completion_date || '',
      status: it.status || 'planned',
      progress_pct: it.progress_pct || 0,
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this plan item?')) return;
    try {
      await fetch(`${API}/api/gaca-p1/sms-plan/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  // Build a Gantt window from min(start) → max(target)
  const { gMin, gMax, gDays } = useMemo(() => {
    const dates = [];
    data.items.forEach(it => { if (it.start_date) dates.push(new Date(it.start_date)); if (it.target_date) dates.push(new Date(it.target_date)); });
    if (dates.length === 0) return { gMin: null, gMax: null, gDays: 0 };
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    const days = Math.max(1, Math.round((max - min) / 86400000));
    return { gMin: min, gMax: max, gDays: days };
  }, [data.items]);

  const barFor = (it) => {
    if (!gMin || !it.start_date || !it.target_date) return null;
    const s = new Date(it.start_date); const e = new Date(it.target_date);
    const left = Math.max(0, Math.round(((s - gMin) / 86400000) / gDays * 100));
    const width = Math.max(2, Math.round(((e - s) / 86400000) / gDays * 100));
    const color = it.status === 'completed' ? 'bg-emerald-500'
      : it.status === 'overdue' ? 'bg-rose-500'
      : it.status === 'in_progress' ? 'bg-sky-500'
      : 'bg-slate-600';
    return { left, width, color };
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></div>;

  // Group by element for collapsible Gantt rendering
  const groups = data.items.reduce((acc, it) => { (acc[it.element] = acc[it.element] || []).push(it); return acc; }, {});
  const elementKeys = Object.keys(groups).sort();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30">
                <GanttChart className="w-6 h-6 text-emerald-300" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="plan-title">
                {t('smsPlan.title', 'SMS Implementation Plan')}
              </h1>
            </div>
            <p className="text-slate-400 max-w-3xl">
              {t('smsPlan.subtitle', 'Element-by-element implementation tracker with owners, target dates, and Gantt-style progress. Maps to GACA item 5.1.3 — documented SMS implementation plan.')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={seedBaseline} variant="outline" className="border-slate-700 text-slate-200" data-testid="plan-seed-btn">
              <Sprout className="w-4 h-4 mr-2" /> Seed Baseline
            </Button>
            <Button onClick={fetchAll} variant="outline" className="border-slate-700 text-slate-200" data-testid="plan-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-500" data-testid="plan-add-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="plan-dialog">
                <DialogHeader><DialogTitle>{editing ? 'Edit Plan Item' : 'New Plan Item'}</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                  <div>
                    <Label>Element</Label>
                    <Input data-testid="plan-element" value={form.element} onChange={e => setForm({ ...form, element: e.target.value })} className="bg-slate-950 border-slate-800" />
                  </div>
                  <div>
                    <Label>Sub-element</Label>
                    <Input data-testid="plan-sub-element" value={form.sub_element} onChange={e => setForm({ ...form, sub_element: e.target.value })} className="bg-slate-950 border-slate-800" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea data-testid="plan-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="bg-slate-950 border-slate-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Owner</Label>
                      <Input data-testid="plan-owner" value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} className="bg-slate-950 border-slate-800" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="plan-status" className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Start date</Label>
                      <Input data-testid="plan-start" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="bg-slate-950 border-slate-800" />
                    </div>
                    <div>
                      <Label>Target date</Label>
                      <Input data-testid="plan-target" type="date" value={form.target_date} onChange={e => setForm({ ...form, target_date: e.target.value })} className="bg-slate-950 border-slate-800" />
                    </div>
                    <div>
                      <Label>Completion date</Label>
                      <Input data-testid="plan-completion" type="date" value={form.completion_date} onChange={e => setForm({ ...form, completion_date: e.target.value })} className="bg-slate-950 border-slate-800" />
                    </div>
                  </div>
                  <div>
                    <Label>Progress %</Label>
                    <Input data-testid="plan-progress" type="number" min={0} max={100} value={form.progress_pct} onChange={e => setForm({ ...form, progress_pct: e.target.value })} className="bg-slate-950 border-slate-800" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-500" data-testid="plan-save-btn">{editing ? 'Update' : 'Create'}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Total" value={data.summary.total} testid="plan-total" />
          <KPI label="Completed" value={data.summary.completed} cls="text-emerald-300" testid="plan-completed" />
          <KPI label="In progress" value={data.summary.in_progress} cls="text-sky-300" testid="plan-in-progress" />
          <KPI label="Overdue" value={data.summary.overdue} cls="text-rose-300" testid="plan-overdue" />
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Avg progress</div>
              <div className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="plan-avg-progress">{data.summary.avg_progress_pct}%</div>
              <Progress value={data.summary.avg_progress_pct} className="h-1.5 mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* Gantt by element */}
        {data.items.length === 0 ? (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-8 text-center">
              <GanttChart className="w-10 h-10 mx-auto text-slate-600 mb-2" />
              <div className="text-slate-400 mb-2" data-testid="plan-empty">No plan items yet.</div>
              <div className="text-slate-500 text-sm">Click "Seed Baseline" to populate a 30-row GACA-aligned starter plan, or "Add Item" to create your own.</div>
            </CardContent>
          </Card>
        ) : (
          elementKeys.map(el => (
            <Card key={el} className="bg-slate-900/60 border-slate-800" data-testid={`plan-element-${el.replace(/[^a-z0-9]/gi, '_')}`}>
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center justify-between">
                  <span>{el}</span>
                  <Badge className="bg-slate-800 text-slate-300 border border-slate-700">{groups[el].length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {groups[el].map(it => {
                    const bar = barFor(it);
                    const status = STATUSES.find(s => s.v === it.status);
                    return (
                      <div key={it.id} className="grid grid-cols-12 gap-3 items-center text-sm py-1.5 border-b border-slate-800/40 last:border-0" data-testid={`plan-row-${it.id}`}>
                        <div className="col-span-3">
                          <div className="text-slate-200 font-medium">{it.sub_element}</div>
                          <div className="text-slate-500 text-xs">{it.description}</div>
                        </div>
                        <div className="col-span-2 text-slate-400 text-xs">
                          <div>{it.owner || '—'}</div>
                          <div className="text-slate-500">{it.start_date} → {it.target_date}</div>
                        </div>
                        <div className="col-span-5 relative h-6 bg-slate-950/60 rounded border border-slate-800">
                          {bar && (
                            <div
                              className={`absolute top-0.5 bottom-0.5 ${bar.color}/30 border ${bar.color.replace('bg-', 'border-')}/60 rounded`}
                              style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                            >
                              <div
                                className={`h-full ${bar.color} rounded-l opacity-80`}
                                style={{ width: `${Math.min(100, it.progress_pct)}%` }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="col-span-1 text-right">
                          <Badge className={`${status?.cls} text-xs`}>{it.progress_pct || 0}%</Badge>
                        </div>
                        <div className="col-span-1 text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(it)} className="text-sky-400 hover:text-sky-300" data-testid={`plan-edit-${it.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-rose-400 hover:text-rose-300" data-testid={`plan-delete-${it.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

const KPI = ({ label, value, cls = 'text-white', testid }) => (
  <Card className="bg-slate-900/60 border-slate-800">
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-1 ${cls}`} data-testid={testid}>{value}</div>
    </CardContent>
  </Card>
);
